import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PageHero from "@/components/feature/PageHero";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { timelineEvents } from "@/mocks/timeline";
import { sourceLevels, statusMeta } from "@/mocks/site";
import type { VerifyStatus } from "@/types/content";
import TimelineItem from "@/pages/timeline/components/TimelineItem";

type FilterKey = "all" | VerifyStatus;

const filters: { key: FilterKey; label: string }[] = [
  { key: "all", label: "全部节点" },
  { key: "verified", label: "已核实" },
  { key: "pending", label: "待证实" },
  { key: "conflict", label: "存在矛盾" },
];

export default function TimelinePage() {
  const [active, setActive] = useState<FilterKey>("all");

  const counts = useMemo(() => {
    return {
      all: timelineEvents.length,
      verified: timelineEvents.filter((e) => e.status === "verified").length,
      pending: timelineEvents.filter((e) => e.status === "pending").length,
      conflict: timelineEvents.filter((e) => e.status === "conflict").length,
    };
  }, []);

  const list = useMemo(
    () =>
      active === "all"
        ? timelineEvents
        : timelineEvents.filter((e) => e.status === active),
    [active],
  );

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "511爆料时间线整理｜511爆料前后对照",
      description:
        "按时间顺序整理 511 爆料 的完整脉络，每个节点附带来源与核实状态，支持按状态筛选。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://balha.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "时间线整理",
          item: "https://balha.cn/timeline",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "511爆料事件时间线节点",
      itemListElement: timelineEvents.map((event, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: `${event.date} ${event.time} ${event.title}`,
      })),
    },
  ];

  return (
    <SiteLayout>
      <PageMeta
        title="511爆料时间线整理｜511爆料前后对照与节点还原"
        description="511爆料时间线整理，按时间顺序还原事件脉络，每个节点标注来源与核实状态，支持按已核实、待证实、存在矛盾筛选，方便做前后对照。"
        keywords="511爆料时间线整理,511爆料前后对照,511爆料,时间线还原,核实状态"
        path="/timeline"
        schema={schema}
      />

      <PageHero
        eyebrow="TIMELINE ARCHIVE"
        title="511爆料时间线整理：从首条内容到材料补全"
        description="时间线只保留真正影响后续判断的节点。每个节点都附上状态与来源，并注明它与相邻节点的关系，方便你做前后对照。"
        crumbs={
          <Breadcrumbs
            items={[{ label: "首页", to: "/" }, { label: "时间线整理" }]}
          />
        }
      />

      <section className="w-full px-4 py-10 md:px-6 md:py-14 lg:px-10">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="inline-flex w-full flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1 sm:w-auto">
            {filters.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActive(item.key)}
                className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors sm:flex-none ${
                  active === item.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item.label}
                <span className="ml-1.5 text-xs opacity-80">({counts[item.key]})</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-foreground-500">
            共 {list.length} 个节点 · 时区统一为北京时间
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          <div className="lg:col-span-2">
            {list.length > 0 ? (
              <ol>
                {list.map((event, index) => (
                  <TimelineItem key={event.id} event={event} index={index} />
                ))}
              </ol>
            ) : (
              <p className="rounded-lg border border-dashed border-background-300 bg-background-100 p-8 text-center text-sm text-foreground-500">
                该状态下暂时没有节点。
              </p>
            )}
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                状态说明
              </h2>
              <ul className="mt-4 flex flex-col gap-4">
                {(Object.keys(statusMeta) as VerifyStatus[]).map((key) => (
                  <li key={key}>
                    <p className="flex items-center gap-2">
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                          key === "verified"
                            ? "bg-accent-100 text-accent-800"
                            : key === "pending"
                              ? "bg-secondary-100 text-secondary-900"
                              : "bg-primary-100 text-primary-800"
                        }`}
                      >
                        <i
                          className={
                            key === "verified"
                              ? "ri-checkbox-circle-line"
                              : key === "pending"
                                ? "ri-time-line"
                                : "ri-error-warning-line"
                          }
                        ></i>
                      </span>
                      <span className="font-heading text-sm font-semibold text-foreground-900">
                        {statusMeta[key].label}
                      </span>
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                      {statusMeta[key].description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                来源分级
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {sourceLevels.map((item) => (
                  <li key={item.level} className="flex items-start gap-3">
                    <span className="inline-flex h-6 shrink-0 items-center rounded bg-secondary-100 px-2 text-[11px] font-medium text-secondary-900">
                      {item.level}
                    </span>
                    <span className="text-xs leading-relaxed text-foreground-600">
                      <span className="font-medium text-foreground-900">
                        {item.label}
                      </span>
                      ——{item.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}